/**
 * Bulk recovery — one plan over the invoices a manager has selected.
 *
 * The figures are computed server-side from the stage-transition history, so
 * the same selection produces the same plan tomorrow. Cognara's covering note
 * is optional, written over those figures, and clearly separated from them.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Download, Loader2, Sparkles, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { int, money, num } from './iptTheme';
import { Btn, Drawer, Empty, Sample, Stat } from './IptPrimitives';

const SEV_TONE = (T) => ({ CRITICAL: T.red, HIGH: T.amber, MEDIUM: T.muted });

export const IptRecoveryDialog = ({ invoiceIds, projectId, T, onClose }) => {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [asking, setAsking] = useState(false);
  const timer = useRef(null);
  const sev = SEV_TONE(T);

  useEffect(() => {
    api.post('/ipt/recovery/plan', { invoice_ids: invoiceIds, project_id: projectId })
      .then((r) => setPlan(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Could not build the plan'));
  }, [invoiceIds, projectId]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const watch = useCallback((briefId) => {
    const tick = async () => {
      try {
        const r = await api.get(`/ipt/analytics/cognara/job/${briefId}`);
        setNote(r.data);
        if (['ready', 'failed'].includes(r.data?.status)) { setAsking(false); return; }
      } catch (e) { /* transient — keep watching */ }
      timer.current = setTimeout(tick, 3500);
    };
    timer.current = setTimeout(tick, 2500);
  }, []);

  const askCognara = async () => {
    setAsking(true);
    try {
      const r = await api.post(`/ipt/recovery/plan/${plan.plan_id}/narrative`, {});
      setNote(r.data);
      if (r.data?.status === 'ready') { setAsking(false); return; }
      watch(r.data.brief_id);
    } catch (e) {
      setAsking(false);
      toast.error(e?.response?.data?.detail || 'Cognara is not available');
    }
  };

  const exportCsv = async () => {
    const r = await api.get(`/ipt/recovery/plan/${plan.plan_id}/export`,
      { responseType: 'blob' });
    const u = URL.createObjectURL(r.data);
    const a = window.document.createElement('a');
    a.href = u; a.download = `recovery_${plan.plan_id}.csv`;
    window.document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 4000);
  };

  const roll = plan?.rollup || {};
  const cur = roll.currency || 'INR';
  const brief = note?.brief;

  return (
    <Drawer open onClose={onClose} T={T} width={980} testId="ipt-recovery-drawer"
      title="Recovery plan"
      subtitle={<p className="text-[11px] mt-0.5" style={{ color: T.muted }}>
        {plan ? `${int(plan.actionable)} open of ${int(plan.invoices)} selected · plan ${plan.plan_id}`
          : 'building from the stage history…'}
      </p>}>
      {error && (
        <div className="flex items-start gap-2 text-[11.5px] py-2" data-testid="ipt-recovery-error"
          style={{ color: T.red }}>
          <AlertTriangle className="w-4 h-4 mt-px" /> {error}
        </div>
      )}
      {!plan && !error && (
        <div className="py-16 text-center" data-testid="ipt-recovery-loading">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: T.primary }} />
        </div>
      )}
      {plan && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            <Stat T={T} testId="ipt-recovery-value" label="Net on the open invoices"
              value={money(roll.netActionable, cur)} sub={`${int(roll.overdue)} past terms`} />
            <Stat T={T} testId="ipt-recovery-at-risk" label="Value at risk"
              value={money(roll.valueAtRisk, cur)} tone={roll.valueAtRisk ? T.red : undefined}
              sub="already beyond payment terms" />
            <Stat T={T} testId="ipt-recovery-days" label="Recoverable days"
              value={`${num(roll.recoverableDays, 1)} d`}
              sub={`${num(roll.vendorSideDays, 1)} d sits with vendors — not recoverable`} />
            <Stat T={T} testId="ipt-recovery-worst" label="Worst beyond terms"
              value={`${num(roll.worstBeyondTerms, 1)} d`}
              tone={roll.critical ? T.red : undefined}
              sub={`${int(roll.critical)} critical`} />
          </div>

          <section data-testid="ipt-recovery-sequence">
            <h4 className="text-[11px] font-bold uppercase tracking-[0.08em] mb-1.5"
              style={{ color: T.muted }}>Who to chase, in order of money held</h4>
            <div className="space-y-1.5">
              {(plan.sequence || []).map((s) => (
                <div key={s.step} data-testid={`ipt-recovery-step-${s.step}`}
                  className="flex items-start gap-3 rounded-md px-3 py-2"
                  style={{ background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
                  <span className="w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold shrink-0"
                    style={{ background: T.primary, color: '#fff' }}>{s.step}</span>
                  <div className="min-w-0">
                    <div className="text-[12px] font-semibold flex items-center gap-1.5"
                      style={{ color: T.ink }}>
                      <Users className="w-3.5 h-3.5" style={{ color: T.muted }} /> {s.owner}
                    </div>
                    <p className="text-[11px]" style={{ color: T.body }}>{s.action}</p>
                  </div>
                  <div className="ml-auto text-right shrink-0">
                    <div className="text-[12px] font-semibold tabular-nums" style={{ color: T.ink }}>
                      {money(s.value, cur)}
                    </div>
                    <div className="text-[9.5px]" style={{ color: T.muted }}>
                      {int(s.invoices)} invoices
                    </div>
                  </div>
                </div>
              ))}
              {!(plan.sequence || []).length && (
                <Empty T={T} testId="ipt-recovery-nothing">
                  Nothing to recover — every invoice selected is already paid or rejected.
                </Empty>
              )}
            </div>
          </section>

          <section data-testid="ipt-recovery-blockers">
            <h4 className="text-[11px] font-bold uppercase tracking-[0.08em] mb-1.5"
              style={{ color: T.muted }}>What is actually blocking them</h4>
            <div className="grid md:grid-cols-2 gap-1.5">
              {(plan.byBlocker || []).map((b) => (
                <div key={b.blocker} data-testid={`ipt-recovery-blocker-${b.blocker}`}
                  className="rounded-md px-3 py-2"
                  style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                  <div className="flex items-center gap-2">
                    <span className="text-[11.5px] font-semibold" style={{ color: T.ink }}>
                      {b.label}
                    </span>
                    <span className="ml-auto text-[11px] tabular-nums" style={{ color: T.body }}>
                      {int(b.count)} · {money(b.value, cur)}
                    </span>
                  </div>
                  <p className="text-[10.5px] mt-0.5" style={{ color: T.muted }}>{b.action}</p>
                </div>
              ))}
            </div>
          </section>

          <section data-testid="ipt-recovery-cognara" className="rounded-lg px-3 py-2.5"
            style={{ background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
            <div className="flex items-center gap-2 flex-wrap">
              <Sparkles className="w-3.5 h-3.5" style={{ color: T.primary }} />
              <span className="text-[11.5px] font-semibold" style={{ color: T.ink }}>
                Cognara covering note
              </span>
              <span className="text-[10px]" style={{ color: T.muted }}>
                written over the figures above — it never replaces them
              </span>
              <span className="ml-auto">
                <Btn T={T} testId="ipt-recovery-ask" disabled={asking} onClick={askCognara}>
                  {asking ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Sparkles className="w-3.5 h-3.5" />}
                  {brief ? 'Rewrite' : 'Write the note'}
                </Btn>
              </span>
            </div>
            {asking && (
              <div className="mt-2" data-testid="ipt-recovery-note-progress">
                <div className="flex items-center justify-between text-[10px] mb-1"
                  style={{ color: T.muted }}>
                  <span>{note?.stage || 'starting'}</span>
                  <span className="tabular-nums" style={{ color: T.primary }}>
                    {note?.progress || 5}%
                  </span>
                </div>
                <div className="h-1 rounded-full overflow-hidden" style={{ background: T.border }}>
                  <div className="h-full transition-all"
                    style={{ width: `${note?.progress || 5}%`, background: T.primary }} />
                </div>
              </div>
            )}
            {note?.status === 'failed' && (
              <p className="text-[11px] mt-2" style={{ color: T.red }}
                data-testid="ipt-recovery-note-error">{note.error}</p>
            )}
            {brief && (
              <div className="mt-2 space-y-2" data-testid="ipt-recovery-note">
                <p className="text-[12.5px] font-semibold" style={{ color: T.ink }}>
                  {brief.headline}
                </p>
                {(brief.sections || []).map((s) => (
                  <div key={s.id}>
                    <div className="text-[10px] uppercase tracking-[0.08em]"
                      style={{ color: T.muted }}>{s.title}</div>
                    <p className="text-[11.5px]" style={{ color: T.body }}>{s.summary}</p>
                    {(s.insights || []).map((x, i) => (
                      <p key={i} className="text-[11px] pl-3" style={{ color: T.body }}>· {x}</p>
                    ))}
                  </div>
                ))}
                {(brief.actions || []).map((a, i) => (
                  <p key={i} className="text-[11.5px]" style={{ color: T.ink }}>
                    <b>{a.owner}</b> — {a.action}
                    <span style={{ color: T.muted }}> ({a.urgency})</span>
                  </p>
                ))}
                {(brief.caveats || []).map((c, i) => (
                  <p key={i} className="text-[10.5px]" style={{ color: T.muted }}>{c}</p>
                ))}
              </div>
            )}
          </section>

          <section data-testid="ipt-recovery-items">
            <div className="flex items-center gap-2 mb-1.5">
              <h4 className="text-[11px] font-bold uppercase tracking-[0.08em]"
                style={{ color: T.muted }}>Invoice by invoice</h4>
              <span className="ml-auto">
                <Btn T={T} variant="ghost" testId="ipt-recovery-export" onClick={exportCsv}>
                  <Download className="w-3.5 h-3.5" /> CSV
                </Btn>
              </span>
            </div>
            <div className="rounded-lg overflow-x-auto"
              style={{ background: T.surface, border: `1px solid ${T.border}` }}>
              <table className="w-full text-[11px]" data-testid="ipt-recovery-table">
                <thead>
                  <tr style={{ background: T.surfaceAlt, color: T.muted }}>
                    {['Invoice', 'Vendor', 'Stage', 'Blocker', 'Owner', 'Next action',
                      'Net', 'Beyond', 'Recoverable'].map((h, i) => (
                      <th key={h} className={`py-2 px-2 font-semibold whitespace-nowrap ${i >= 6 ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(plan.items || []).map((i) => (
                    <tr key={i.invoice_id} data-testid={`ipt-recovery-row-${i.invoice_no}`}
                      style={{ borderTop: `1px solid ${T.border}`,
                        opacity: i.actionable ? 1 : 0.55 }}>
                      <td className="py-1.5 px-2 whitespace-nowrap font-semibold"
                        style={{ color: T.primary }}>{i.invoice_no}</td>
                      <td className="py-1.5 px-2 truncate max-w-[140px]"
                        style={{ color: T.ink }}>{i.vendor_name}</td>
                      <td className="py-1.5 px-2 whitespace-nowrap"
                        style={{ color: T.body }}>{i.stageLabel}</td>
                      <td className="py-1.5 px-2" style={{ color: sev[i.severity] || T.body }}>
                        {i.blockerLabel}
                      </td>
                      <td className="py-1.5 px-2 whitespace-nowrap"
                        style={{ color: T.body }}>{i.owner}</td>
                      <td className="py-1.5 px-2" style={{ color: T.muted }}>{i.nextAction}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums font-semibold"
                        style={{ color: T.ink }}>{money(i.netPayable, i.currency)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums"
                        style={{ color: i.daysBeyondTerms > 0 ? T.red : T.muted }}>
                        {i.daysBeyondTerms > 0 ? `${num(i.daysBeyondTerms, 0)} d` : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums"
                        style={{ color: T.body }}>{num(i.recoverableDays, 1)} d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <Sample T={T} testId="ipt-recovery-sample" text={`${plan.sample} `
            + plan.definitions.recoverable} />
        </>
      )}
    </Drawer>
  );
};

export default IptRecoveryDialog;
